import { useState, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  MagnifyingGlassIcon,
  ClipboardDocumentCheckIcon,
  ArrowDownTrayIcon,
  ArrowPathIcon,
  ChevronDownIcon,
  ShieldCheckIcon,
  UsersIcon,
} from '@heroicons/react/24/outline'
import { api } from '@/lib/api'
import clsx from 'clsx'
import toast from 'react-hot-toast'

interface Props {
  selectedTenantId?: string
  pendingProcessingCount: number
  onScanStarted?: (jobId: string) => void
  onRefresh?: () => void
  isScanning?: boolean
  isRefreshing?: boolean
}

export default function QuickActions({
  selectedTenantId,
  pendingProcessingCount,
  onScanStarted,
  onRefresh,
  isScanning = false,
  isRefreshing = false
}: Props) {
  const navigate = useNavigate()
  const [isScanLoading, setIsScanLoading] = useState(false)
  const [isSecureScoreRefreshing, setIsSecureScoreRefreshing] = useState(false)
  const [isUserInsightsRefreshing, setIsUserInsightsRefreshing] = useState(false)
  const [isDownloading, setIsDownloading] = useState(false)
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleCheckForDrift = async () => {
    if (!selectedTenantId || isScanLoading || isScanning) return

    setIsDropdownOpen(false)
    setIsScanLoading(true)
    try {
      const result = await api.scanTenant(selectedTenantId)
      onScanStarted?.(result.jobId)
      toast.success('Drift check started')
    } catch (error) {
      console.error('Failed to start drift check:', error)
      toast.error('Failed to start drift check')
    } finally {
      setIsScanLoading(false)
    }
  }

  const handleRefreshSecureScore = async () => {
    if (!selectedTenantId || isSecureScoreRefreshing) return

    setIsDropdownOpen(false)
    setIsSecureScoreRefreshing(true)
    try {
      await api.refreshSecureScore(selectedTenantId)
      toast.success('Secure Score refresh started')
      onRefresh?.()
    } catch (error) {
      console.error('Failed to refresh Secure Score:', error)
      toast.error('Failed to refresh Secure Score')
    } finally {
      setIsSecureScoreRefreshing(false)
    }
  }

  const handleRefreshUserInsights = async () => {
    if (!selectedTenantId || isUserInsightsRefreshing) return

    setIsDropdownOpen(false)
    setIsUserInsightsRefreshing(true)
    try {
      await api.refreshUserInsights(selectedTenantId)
      toast.success('User Analytics refresh started')
      onRefresh?.()
    } catch (error) {
      console.error('Failed to refresh User Analytics:', error)
      toast.error('Failed to refresh User Analytics')
    } finally {
      setIsUserInsightsRefreshing(false)
    }
  }

  const handleViewPendingProcessing = () => {
    navigate('/configuration-drift?status=Approved')
  }

  const handleDownloadBaseline = async () => {
    if (!selectedTenantId || isDownloading) return

    setIsDownloading(true)
    try {
      const blob = await api.downloadBaselineReport(selectedTenantId, 'html')
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `baseline-report-${selectedTenantId}.html`
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
    } catch (error) {
      console.error('Failed to download baseline:', error)
      toast.error('Failed to download baseline')
    } finally {
      setIsDownloading(false)
    }
  }

  // Consistent button base styles
  const buttonBase = 'inline-flex items-center justify-center h-9 px-3.5 text-sm font-medium rounded-lg transition-all duration-200 whitespace-nowrap'

  // Primary button (Actions dropdown)
  const primaryStyles = clsx(
    buttonBase,
    'bg-gradient-to-r from-primary-600 to-primary-500 text-white shadow-glow-cyan',
    'hover:shadow-glow-cyan-lg hover:-translate-y-0.5',
    'disabled:from-dark-600 disabled:to-dark-600 disabled:shadow-none disabled:cursor-not-allowed'
  )

  // Secondary button (Download Baseline, Refresh)
  const secondaryStyles = clsx(
    buttonBase,
    'bg-dark-800 text-slate-300 border border-dark-600',
    'hover:border-primary-500/50 hover:text-primary-400 hover:bg-dark-700/50',
    'disabled:bg-dark-900 disabled:text-slate-600 disabled:border-dark-700 disabled:cursor-not-allowed'
  )

  // Warning/attention button (Commit Changes with items)
  const warningStyles = clsx(
    buttonBase,
    'bg-gradient-to-r from-orange-600 to-amber-500 text-white',
    'shadow-[0_0_15px_rgba(251,146,60,0.4)] hover:shadow-[0_0_25px_rgba(251,146,60,0.6)]',
    'hover:-translate-y-0.5 animate-pulse'
  )

  const isAnyActionLoading = isScanLoading || isScanning || isSecureScoreRefreshing || isUserInsightsRefreshing

  return (
    <div className="flex items-center gap-2">
      {/* Actions Dropdown - Primary action */}
      <div className="relative" ref={dropdownRef}>
        <button
          onClick={() => setIsDropdownOpen(!isDropdownOpen)}
          disabled={!selectedTenantId || isAnyActionLoading}
          className={primaryStyles}
        >
          {isAnyActionLoading ? (
            <ArrowPathIcon className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <MagnifyingGlassIcon className="h-4 w-4 mr-2" />
          )}
          {isScanLoading || isScanning ? 'Checking...' :
           isSecureScoreRefreshing ? 'Refreshing...' :
           isUserInsightsRefreshing ? 'Refreshing...' : 'Actions'}
          <ChevronDownIcon className={clsx('h-4 w-4 ml-1.5 transition-transform', isDropdownOpen && 'rotate-180')} />
        </button>

        {/* Dropdown Menu */}
        {isDropdownOpen && (
          <div className="absolute right-0 mt-2 w-56 bg-dark-800 border border-dark-600 rounded-lg shadow-xl z-50 overflow-hidden">
            <div className="py-1">
              {/* Check for Drift */}
              <button
                onClick={handleCheckForDrift}
                disabled={isScanLoading || isScanning}
                className="w-full flex items-center px-4 py-2.5 text-sm text-slate-300 hover:bg-dark-700 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <MagnifyingGlassIcon className="h-4 w-4 mr-3 text-primary-400" />
                <div className="text-left">
                  <div className="font-medium">Check for Drift</div>
                  <div className="text-xs text-slate-500">Compare config against baseline</div>
                </div>
              </button>

              {/* Refresh Secure Score */}
              <button
                onClick={handleRefreshSecureScore}
                disabled={isSecureScoreRefreshing}
                className="w-full flex items-center px-4 py-2.5 text-sm text-slate-300 hover:bg-dark-700 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <ShieldCheckIcon className="h-4 w-4 mr-3 text-emerald-400" />
                <div className="text-left">
                  <div className="font-medium">Refresh Secure Score</div>
                  <div className="text-xs text-slate-500">Update from Microsoft Graph</div>
                </div>
              </button>

              {/* Refresh User Analytics */}
              <button
                onClick={handleRefreshUserInsights}
                disabled={isUserInsightsRefreshing}
                className="w-full flex items-center px-4 py-2.5 text-sm text-slate-300 hover:bg-dark-700 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <UsersIcon className="h-4 w-4 mr-3 text-violet-400" />
                <div className="text-left">
                  <div className="font-medium">Refresh User Analytics</div>
                  <div className="text-xs text-slate-500">Update license & MFA data</div>
                </div>
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Commit Changes - Only show when count > 0 */}
      {pendingProcessingCount > 0 && (
        <button
          onClick={handleViewPendingProcessing}
          className={warningStyles}
        >
          <ClipboardDocumentCheckIcon className="h-4 w-4 mr-2" />
          Commit Changes
          <span className="ml-2 inline-flex items-center justify-center px-1.5 min-w-[1.25rem] h-5 text-xs font-bold bg-white/20 rounded-full">
            {pendingProcessingCount}
          </span>
        </button>
      )}

      {/* Download Baseline */}
      <button
        onClick={handleDownloadBaseline}
        disabled={!selectedTenantId || isDownloading}
        className={secondaryStyles}
      >
        {isDownloading ? (
          <ArrowPathIcon className="h-4 w-4 mr-2 animate-spin" />
        ) : (
          <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
        )}
        Download Baseline
      </button>

      {/* Refresh */}
      {onRefresh && (
        <button
          onClick={onRefresh}
          disabled={isRefreshing}
          className={secondaryStyles}
        >
          <ArrowPathIcon className={clsx('h-4 w-4 mr-2', isRefreshing && 'animate-spin')} />
          Refresh
        </button>
      )}
    </div>
  )
}
